/**
 * Visual / semantic state for the `<app-due-in>` renderer.
 *
 * `unknown` is the "no SLA" state — rendered as `—` with no
 * coloured class. Maps to `dueDateUtc = null` from the API.
 */
export enum DueInState {
  Overdue = "overdue",
  Soon = "soon",
  Later = "later",
  Unknown = "unknown",
}

export interface DueInRender {
  state: DueInState;
  label: string;
}

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;

/**
 * Pure helper that turns a `dueDateUtc` ISO string (or null) into the
 * label and state class used by `<app-due-in>`. Separated from the
 * component so the state spec stays render-agnostic and the component
 * spec stays DOM-focused.
 *
 * Labels:
 * - null / invalid → `—` + `DueInState.Unknown`
 * - past deadline → `"Overdue Xh"` + `DueInState.Overdue`
 * - <24h remaining → `"Due in Xh"` + `DueInState.Soon`
 * - ≥24h remaining → `"Due in Xd Xh"` (or `"Due in Xd"`) + `DueInState.Later`
 *
 * @param dueDateUtc - ISO string from the API (`o.DueDateUtc as dueDateUtc`).
 * @param now - Reference instant; defaults to `Date.now()`.
 *   Pass explicitly in tests.
 * @returns Object with `state` enum and the rendered `label` text.
 */
export function computeDueInState(
  dueDateUtc: string | null | undefined,
  now: number = Date.now(),
): DueInRender {
  if (!dueDateUtc) {
    return { state: DueInState.Unknown, label: "—" };
  }
  const due = new Date(dueDateUtc).getTime();
  if (Number.isNaN(due)) {
    return { state: DueInState.Unknown, label: "—" };
  }

  const diffMs = due - now;
  const overdue = diffMs <= 0;
  const absMs = Math.abs(diffMs);
  const totalHours = Math.floor(absMs / HOUR_MS);
  const days = Math.floor(absMs / DAY_MS);
  const remainderHours = totalHours % 24;

  const prefix = overdue ? "Overdue" : "Due in";

  let body: string;
  if (totalHours < 24) {
    body = `${totalHours}h`;
  } else if (days <= 2) {
    body = remainderHours > 0 ? `${days}d ${remainderHours}h` : `${days}d`;
  } else {
    body = `${days}d`;
  }

  let state: DueInState;
  if (overdue) {
    state = DueInState.Overdue;
  } else if (totalHours < 24) {
    state = DueInState.Soon;
  } else {
    state = DueInState.Later;
  }

  return { state, label: `${prefix} ${body}` };
}
